import { Show, MediaItem, AdminUser, Venue } from '../types';

// API configuration
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:7071/api'
  : '/api';


// Helper function for API calls
const apiCall = async (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('authToken');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Authorization header added above if token present

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null;
  }

  return response.json();
};

// Mock media data (until we implement media storage)
const mockMedia: MediaItem[] = [
  {
    id: '1',
    type: 'photo',
    title: 'Band Photo 1',
    url: '/images/hero1.jpg',
    order: 1
  },
  {
    id: '2',
    type: 'video',
    title: 'Live Performance',
    url: 'https://www.youtube.com/embed/example1',
    order: 2
  },
  {
    id: '3',
    type: 'soundcloud',
    title: 'Sample Track',
    url: 'https://soundcloud.com/example/track',
    order: 3
  }
];

export const api = {
  // Auth
  login: async (email: string, password: string): Promise<AdminUser> => {
    const response = await apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (response.token) {
      localStorage.setItem('authToken', response.token);
    }

    return response.user;
  },

  loginWithGoogle: async (credential: string): Promise<AdminUser> => {
    try {
      const response = await apiCall('/auth/google', {
        method: 'POST',
        body: JSON.stringify({ credential }),
      });

      if (response.token) {
        localStorage.setItem('authToken', response.token);
      }

      return response.user;
    } catch (error: any) {
      // Re-throw with more specific error message
      if (error.message?.includes('Access denied')) {
        throw new Error('Access denied: Your email is not authorized');
      }
      throw error;
    }
  },

  verifyToken: async (): Promise<AdminUser | null> => {
    try {
      const response = await apiCall('/auth/verify', {
        method: 'POST',
      });
      return response.user;
    } catch (error) {
      return null;
    }
  },

  logout: async (): Promise<void> => {
    try {
      await apiCall('/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem('authToken');
  },

  // Shows
  getShows: async (): Promise<Show[]> => {
    try {
      const shows = await apiCall('/shows');
      // Keep date as string to avoid timezone issues
      return shows;
    } catch (error) {
      console.error('Error fetching shows:', error);
      // Return empty array if API fails
      return [];
    }
  },

  createShow: async (show: Omit<Show, 'id'>): Promise<Show> => {
    // Convert Date object to ISO string for API
    const showData = {
      ...show,
      date: show.date instanceof Date ? show.date.toISOString() : show.date
    };
    const response = await apiCall('/shows', {
      method: 'POST',
      body: JSON.stringify(showData),
    });
    return response;
  },

  updateShow: async (id: string, show: Partial<Show>): Promise<Show> => {
    // Convert Date object to ISO string for API
    const showData = show.date ? {
      ...show,
      date: show.date instanceof Date ? show.date.toISOString() : show.date
    } : show;
    const response = await apiCall(`/shows/${id}`, {
      method: 'PUT',
      body: JSON.stringify(showData),
    });
    return response;
  },

  deleteShow: async (id: string): Promise<void> => {
    await apiCall(`/shows/${id}`, {
      method: 'DELETE',
    });
  },

  // Media (still using mock data for now)
  getMedia: async (): Promise<MediaItem[]> => {
    // TODO: Implement real media storage with Azure Blob Storage
    return mockMedia;
  },

  // Image Upload (auth required)
  uploadImage: async (base64Image: string): Promise<{ success: boolean; url: string; filename: string; isDataUrl?: boolean }> => {
    const response = await apiCall('/upload/image', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: base64Image })
    });
    return response;
  },

  uploadMedia: async (file: File, type: MediaItem['type']): Promise<MediaItem> => {
    // TODO: Implement real file upload to Azure Blob Storage
    // For now, create a local URL
    const newMedia: MediaItem = {
      id: Date.now().toString(),
      type,
      title: file.name,
      url: URL.createObjectURL(file),
      order: mockMedia.length + 1
    };
    mockMedia.push(newMedia);
    return newMedia;
  },

  // Venues
  getVenues: async (): Promise<Venue[]> => {
    try {
      return await apiCall('/venues');
    } catch (error) {
      console.error('Error fetching venues:', error);
      return [];
    }
  },

  createVenue: async (venue: Omit<Venue, 'id'>): Promise<Venue> => {
    return await apiCall('/venues', {
      method: 'POST',
      body: JSON.stringify(venue),
    });
  },

  updateVenue: async (id: string, venue: Partial<Venue>): Promise<Venue> => {
    return await apiCall(`/venues/${id}`, {
      method: 'PUT',
      body: JSON.stringify(venue),
    });
  },

  deleteVenue: async (id: string): Promise<void> => {
    await apiCall(`/venues/${id}`, {
      method: 'DELETE',
    });
  },

  // Booking
  submitBooking: async (bookingData: {
    name: string;
    email: string;
    phone: string;
    eventDate: string;
    eventType: string;
    soundProvided: boolean;
    estimatedGuests: string;
    indoorOutdoor: string;
    budget: string;
    desiredBandSize: string;
    message: string;
  }): Promise<{ success: boolean; message: string; bookingId?: string }> => {
    return await apiCall('/booking', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }
};
